import {useContext, useEffect, useMemo, useState} from "react";
import { renderToString } from 'react-dom/server';
import { Link, useLocation } from "react-router-dom";
import './_header-nav.scss';
import { patientHeaderNavegationItems } from "@/router/patientPageData";
import {navArray} from "@/router/navArray";
import mobileMenu from './images/icon-header-mobile-menu.svg';
import { LayoutContext } from "@/components/layout-context-container";
import PatientButtonInterstitial from "@/components/button-interstitial";
import CustomImage from "@/components/image";

function PatientHeaderNav({mobile = false, customClass = "", setSubnavGroupSize = null, ...rest}) {
  const { showMobileMenu, setShowMobileMenu, stickyHeaderFixedState } = useContext(LayoutContext);
  const Location = useLocation();
  const currentPath = useMemo(() => {
    return Location.pathname;
  }, [Location]);
  const [ currentNavGroupId, _setCurrentNavGroupId ] = useState(-1);
  function setCurrentNavGroupId (id) {
    _setCurrentNavGroupId(id);
    setSubnavGroupSize && setSubnavGroupSize(id === -1 ? null : id);
  }
  
  const containerClass = useMemo(() => {
    var className = 'patient-header-nav';

    if (showMobileMenu) {
      className = `${className} patient-header-nav--menu-show`;
    }

    if (mobile) {
      className = `${className} patient-header-nav--mobile`;
    }

    if (customClass) {
      className = `${className} ${customClass}`;
    }

    return className;
  }, [showMobileMenu, mobile, customClass]);

  const navigationItems = useMemo(() => {
    const navItemsFromRouter = Object.values(patientHeaderNavegationItems);

    return navArray({
      navItemsFromRouter
    });
  }, []);

  // reset subnav when the sticky nav is set to fixed
  useEffect(() => {
    if (stickyHeaderFixedState) {
      setCurrentNavGroupId(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyHeaderFixedState]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 1024) {
        // auto close sub menu if opened from desktop and screen becomes less than tablet size
        setCurrentNavGroupId(-1);
      }
    }

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // close sub nav when there is a click even outside the nav
  useEffect(() => {
    function handleDocumentClick(e) {
      // fix dropdown click on mobile
      const isInsideDropdown = e.target.closest('.has-dropdown') || 
                              e.target.closest('.patient-header-nav__item-subnav');
      if (isInsideDropdown) {
        return;
      }

      const group = e.target.dataset?.group;

      if(parseInt(group) !== currentNavGroupId){
        document.removeEventListener('click', handleDocumentClick);
        setCurrentNavGroupId(-1);
      }
    }
    document.addEventListener('click', handleDocumentClick);

    return () => document.removeEventListener('click', handleDocumentClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNavGroupId]);

  const getNavStateClasses = (page) => {
    let className =
      (page.path === '/' && currentPath === '/') ||
      (page.path !== '/' && currentPath.startsWith(page.path) && (page.path.split("/").length -1) < 3 && !mobile) ||
      (page.subPages && page.subPages.some(subPage => subPage.path === currentPath) && !mobile) ||
      (page.path.replace(/\/$/, "") === currentPath.replace(/\/$/, ""))
        ? ` current`
        : '';
    if (currentNavGroupId !== undefined && page.subPages) {
      const showSubnav = currentNavGroupId === page.groupId ? ' show-subnav' : '';
      className = `${className}${showSubnav}`;
    }
    return className;
  }

  function handleShowHidMobileMenu() {
    setShowMobileMenu(!showMobileMenu);
  }

  function handleNavClick(e) {
    const group = e.currentTarget.dataset?.group;
    const subnav =  e.currentTarget.dataset?.nav === "subnav";
    const currentGroupSize = e.currentTarget.dataset?.groupSize ? parseInt(e.currentTarget.dataset.groupSize) : null;

    if ((!subnav && parseInt(group) === currentNavGroupId) || subnav){
      if (e.type === 'mouseover') {
          //
      } else {
        setCurrentNavGroupId(-1);
      }
    } else {
      setCurrentNavGroupId(parseInt(group));
      setSubnavGroupSize && setSubnavGroupSize(currentGroupSize);
    }

    const hasDropdown = e.currentTarget.classList.contains('has-dropdown');

    // Only close mobile menu if it's not a dropdown toggle AND we're on mobile
    if (!hasDropdown && mobile) {
      setShowMobileMenu(!showMobileMenu);
    }
    // For dropdown toggles on mobile, don't close the mobile menu
    if (hasDropdown && mobile) {
      // Prevent navigation for dropdown toggles
      e.preventDefault();

      const parent = document.querySelector(`.patient-header-nav__item.group-id-${group}`);
      const parentGroup = document.querySelector(`.patient-header-nav__item--group.group-id-${group}`);
      const showSubnav =  parent?.classList?.contains('show-subnav');

      if (showSubnav) {
        parent?.classList.remove('show-subnav');
        parentGroup?.classList.remove('show-subnav');
      } else {
        parent?.classList.add('show-subnav');
        parentGroup?.classList.add('show-subnav');
      }
    }
  }

  const HealthCareProfessionalsLink = () => { return (
    <PatientButtonInterstitial
      variant={"anchorlink"}
      customClass="patient-header-nav__hcp-link"
      buttonUrl="/hcp"
      preventDefault
      useInline
      data-gtm-event-name="Header"
      data-gtm-event-category="Navigation"
      data-gtm-event-action="Click"
      data-gtm-event-label="For Healthcare Professionals"
    >
      <span><span className="text-nowrap">For Healthcare</span> Professionals</span>
    </PatientButtonInterstitial>
  );};

  return (
    <nav className={containerClass} aria-label='Primary Navigation' {...rest}>
      <div className="patient-header-nav__items" role="menubar">
        {mobile && <div
           className={`patient-header-nav__item${getNavStateClasses({path: '/'})}`}
        >
          <Link
            to="/"
            onClick={handleNavClick}
            className="home"
            data-gtm-event-name="Header"
            data-gtm-event-category="Navigation"
            data-gtm-event-action="Click"
            data-gtm-event-label="HOME"
            role="menuitem"
          >Home</Link>
        </div>}

        {navigationItems.map((page, i) => (
          <div className={`patient-header-nav__item${!mobile ? getNavStateClasses(page) : ''} ${!mobile ? '' : 'group-id-' + page.groupId}`.trim()}
               key={`key_header_nav_item_container_${i}`}
          >
            {mobile && page.subPages && <div
              className={`patient-header-nav__item--group${!mobile ? getNavStateClasses(page) : ''} ${!mobile ? '' : 'group-id-' + page.groupId}`.trim()}
              key={`key_header_nav_item_${i}`}
            >
              <Link
                to={page.subPages ? undefined : page.path}
                onClick={handleNavClick}
                className={page.subPages ? "has-dropdown" : ""}
                data-group={page.groupId}
                data-group-size={page.subPages ? page.subPages.length : 0}
                data-gtm-event-name="Header"
                data-gtm-event-category="Navigation"
                data-gtm-event-action="Click"
                data-gtm-event-label={renderToString(page.name).replace(/<[^>]*>/g, '')}
                role="menuitem"
              >{page.name}</Link>
            </div>}
            {(!mobile || (mobile && !page.subPages) )&& <Link
              className={`${page.subPages ? "has-dropdown" : ""} ${(page.path === currentPath) && mobile ? 'current' : ''}`}
              // add class current if we are currently on the page
              key={`key_header_nav_item_${i}`}
              to={page.subPages ? undefined : page.path}
              onClick={handleNavClick}
              onMouseOver={page.subPages && !mobile ? handleNavClick : undefined}
              data-group={page.groupId}
              data-group-size={page.subPages ? page.subPages.length : 0}
              data-gtm-event-name="Header"
              data-gtm-event-category="Navigation"
              data-gtm-event-action="Click"
              data-gtm-event-label={page.path === '/' ? "HOME" : renderToString(page.name).replace(/<[^>]*>/g, '')}
              role="menuitem"
            >{page.name}</Link>}

            {page.subPages
              ? <div
                  className = "patient-header-nav__item-subnav"
                  data-group={page.groupId}
                  aria-label="Subnavigation"
                  role="menu"
                  // aria-hidden={currentNavGroupId !== page.groupId}
                >
                  {page.subPages.map((subpage, x) => (

                    <Link
                      className={getNavStateClasses(subpage)}
                      key={`key_header_sub_nav_item_${x}`}
                      to={subpage.path}
                      onClick={handleNavClick}
                      data-group={page.groupId}
                      data-nav="subnav"
                      data-gtm-event-name="Header"
                      data-gtm-event-category="Navigation"
                      data-gtm-event-action="Click"
                      data-gtm-event-label={ renderToString(subpage.name).replace(/<[^>]*>/g, '')}
                      draggable="false"
                      role="menuitem"
                    >{subpage.name}</Link>
                  ))}
                </div>
              : ''}
          </div>
        ))}

        {mobile && <div className={`patient-header-nav__item`}>
          <HealthCareProfessionalsLink />
        </div>}

        {!mobile && <HealthCareProfessionalsLink />}
      </div>
      {!mobile && <div className="patient-header-nav__button-mobile-menu">
        <button
          type="button"
          onClick={handleShowHidMobileMenu}
          role="menuitem"
          data-gtm-event-name="Header"
          data-gtm-event-category="Navigation"
          data-gtm-event-action="Click"
          data-gtm-event-label={showMobileMenu ? "Mobile Menu Close" : "Mobile Menu Open"}
        >
          <CustomImage src={mobileMenu} alt="Open menu" width={45} height={45} />
        </button>
      </div>}
    </nav>
  );
}

export default PatientHeaderNav;